import { useEffect } from "react";
import {
    Phone,
    ArrowUpRight,
    Check,
    Hammer,
    Wrench,
    Ruler,
    Layers,
    Award,
    ShieldCheck,
    Clock,
    DollarSign,
    LayoutGrid,
    TrendingUp,
    Sparkles,
    Home as HomeIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema, serviceSchema } from "@/lib/seo";

// Real deck photos sourced from Wikimedia Commons (CC-licensed) — credited at bottom.
const IMG_HERO = "/images/decks/backyard-deck.jpg";
const IMG_BUILDING = "/images/decks/building-deck.jpg";
const IMG_FINISHED = "/images/decks/finished-deck.jpg";
const IMG_REPAIR = "/images/decks/deck-maintenance.jpg";

const BUILD_SERVICES = [
    { i: Ruler, t: "Custom Deck Design", d: "We design around your house, your view, your yard, and your budget — not a catalog drawing." },
    { i: Hammer, t: "New Deck Construction", d: "Code-compliant footings, posts, beams, joists, ledgers, and decking — built to outlast the house." },
    { i: TrendingUp, t: "Deck Additions", d: "Bumping out, wrapping around, or adding a second level to your existing deck." },
    { i: Layers, t: "Multi-Level Decks", d: "Step-down decks for sloped Southern Oregon lots — fully engineered, no wobble." },
    { i: Sparkles, t: "Composite & Wood", d: "Trex / TimberTech composites, cedar, pressure-treated, or Pacific NW hardwoods." },
    { i: LayoutGrid, t: "Railings & Stairs", d: "Wood, metal, cable, or composite railings. Code-rated stair stringers and treads." },
];

const REPAIR_SERVICES = [
    { i: Sparkles, t: "Deck Resurfacing", d: "Pull and replace the deck boards while keeping the existing frame — fresh deck for half the price." },
    { i: Hammer, t: "Board Replacement", d: "Targeted board swaps for rot, splits, or cracked boards. Color-matched and re-fastened tight." },
    { i: ShieldCheck, t: "Structural Repairs", d: "Joist, ledger, post, beam, and footing repairs. We diagnose first, then fix what's actually wrong." },
    { i: LayoutGrid, t: "Railing Fixes", d: "Loose pickets, wobbling posts, rotted top caps — re-anchored to current code." },
    { i: TrendingUp, t: "Stair Repairs", d: "Cracked stringers, loose treads, rotted bottoms. Often the most-used and most-neglected part of a deck." },
    { i: Wrench, t: "Post Reinforcement", d: "Failing posts sistered, replaced, or reinforced with proper hardware. No more bouncy deck." },
];

const WARNING_SIGNS = [
    "Boards feel spongy or flex under your weight",
    "Railings wobble or pickets are loose",
    "Visible cracks, splits, or rot in deck boards",
    "Posts feel loose where they meet the deck or ground",
    "Stairs creak, bounce, or feel uneven",
    "Nails or screws are popping up out of the boards",
    "Mold, mildew, or black staining across the surface",
    "The ledger looks separated from the house",
];

const TRUST = [
    { i: ShieldCheck, t: "Reliable", d: "You can count on us — quoted day, scheduled day, finished day." },
    { i: Award, t: "Quality", d: "Workmanship you can trust. 25 years on the tools, CCB-licensed." },
    { i: Clock, t: "On Time", d: "We respect your time and your schedule." },
    { i: DollarSign, t: "Fair Pricing", d: "Real numbers up front. No surprises, just honest work." },
];

const MATERIALS = [
    { name: "Pressure-Treated Pine", life: "15–20 yr", look: "Classic, paintable", price: "$" },
    { name: "Cedar", life: "20–25 yr", look: "Warm, knotty, ages silver", price: "$$" },
    { name: "Composite (Trex etc.)", life: "25–30+ yr", look: "Uniform, low-maintenance", price: "$$$" },
    { name: "PNW Hardwoods", life: "30+ yr", look: "Rich grain, premium feel", price: "$$$$" },
];

export default function Decks() {
    useSeo({
        title: "Deck Builder & Deck Repair · Medford & Southern Oregon | 5 Star Construction",
        description: "Custom deck builder and deck repair contractor in Medford, Ashland, and the Rogue Valley. New decks, additions, structural repairs, resurfacing, railings & stairs. Wood, composite, and PNW hardwood. Licensed Oregon GC (CCB #232477). Free estimates — 541-292-9620.",
        path: "/decks",
        jsonLdKey: "decks",
        jsonLd: [
            serviceSchema({
                name: "Deck Construction & Deck Repair",
                description: "New deck construction, additions, multi-level decks, deck resurfacing, structural deck repairs, railing fixes, and stair repairs in Medford and Southern Oregon.",
                path: "/decks",
                serviceType: "Deck Builder",
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/#services" },
                { name: "Decks", path: "/decks" },
            ]),
        ],
    });
    useEffect(() => {}, []);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD400] border border-[#FFD400] px-3 py-1.5">
                            <Hammer className="w-4 h-4 text-[#111111]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase text-[#111111]">Decks &amp; Deck Repairs · Medford, OR</span>
                        </div>
                        <h1 className="font-['Anton'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl uppercase">
                            Decks &amp; deck
                            <br />
                            <span className="text-[#FFD400]">repairs.</span>
                        </h1>
                        <p className="mt-5 font-['Anton'] tracking-tight uppercase text-2xl sm:text-3xl text-[#F6F5F2]/95">
                            Built strong. Built <span className="text-[#E11D2A]">right.</span> Built to last.
                        </p>
                        <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-2xl">
                            New decks, deck additions, full structural repairs, board replacements, railings, stairs, and resurfacing. Composite or wood — we build it the way it should&apos;ve been built the first time.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-4">
                            <a href={SITE.phoneHref} data-testid="decks-call-cta">
                                <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                                    <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                            <a href="/#contact" data-testid="decks-quote-cta">
                                <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#F6F5F2] bg-transparent text-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] font-bold tracking-wide text-base font-['Manrope']">
                                    FREE DECK ESTIMATE
                                    <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-4 relative">
                        <div className="relative aspect-square w-full max-w-sm mx-auto lg:max-w-none overflow-hidden border-2 border-[#F6F5F2]/20 shadow-block">
                            <img
                                src={IMG_HERO}
                                alt="Finished residential wood deck with railings, stairs, and planters — Southern Oregon backyard"
                                loading="eager"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute -top-4 -right-4 hidden md:flex w-32 h-32 bg-[#E11D2A] text-white items-center justify-center flex-col border-2 border-[#111111] shadow-block-sm rotate-3">
                                <div className="font-['Anton'] text-xl tracking-tight uppercase leading-none">Free</div>
                                <div className="font-['Anton'] text-2xl tracking-tight uppercase leading-tight">Estimates</div>
                                <div className="font-['Manrope'] text-[9px] uppercase tracking-[0.18em] font-bold mt-1">Call today</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What we build */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-8 mb-12">
                        <div className="lg:col-span-5">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">What we build</div>
                            <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                                New decks.
                                <br />
                                Additions.
                                <br />
                                Done right.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:pt-3">
                            <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Every deck we build starts with a real conversation about how you&apos;ll actually use it — grill spot, hot tub, dining, sun, shade, pets, kids. Then we engineer it around the way your house, yard, and Southern Oregon weather actually behave. No cookie-cutter rectangles.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {BUILD_SERVICES.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6" data-testid={`deck-build-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-12 h-12 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* In-progress / Build showcase */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                            <img
                                src={IMG_BUILDING}
                                alt="Large residential deck under construction — framed-out joists visible around the perimeter, decking nearly complete, attached to the house with sliding doors and steps."
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">How we build</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            From footings to finish — engineered, not improvised.
                        </h2>
                        <ol className="mt-7 space-y-5">
                            {[
                                { t: "Footings & posts", d: "Proper concrete depths for Southern Oregon frost line. Galvanized post bases. No buried wood." },
                                { t: "Beams, joists & ledger", d: "Sized per span tables, hangers everywhere they belong, ledger flashed and lagged to solid framing." },
                                { t: "Decking & fasteners", d: "Hidden clips for composite, stainless or coated screws for wood. No popped nails six months later." },
                                { t: "Railings, stairs & finish", d: "Code-rated 36\" rails (42\" if it's above 30\"), 4\" picket spacing, proper stringer math." },
                            ].map((s, idx) => (
                                <li key={s.t} className="flex gap-5 items-start" data-testid={`deck-step-${idx}`}>
                                    <div className="shrink-0 w-12 h-12 bg-[#111111] text-[#FFD400] flex items-center justify-center font-['Anton'] text-xl">
                                        0{idx + 1}
                                    </div>
                                    <div>
                                        <div className="font-display text-lg md:text-xl font-extrabold text-[#111812]">{s.t}</div>
                                        <p className="font-['Manrope'] text-base text-[#5F5F5F] mt-1 leading-relaxed">{s.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* Repairs */}
            <section className="bg-white border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid lg:grid-cols-12 gap-12 items-start mb-14">
                        <div className="lg:col-span-7">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Deck repairs</div>
                            <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                                Don&apos;t tear it out. <span className="text-[#E11D2A]">Repair it right.</span>
                            </h2>
                            <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                                Most decks we see don&apos;t need to be replaced — they need the rotted parts fixed, the loose parts re-anchored, and the surface refreshed. We diagnose the structure first, fix what&apos;s actually wrong, and save you the cost of starting over.
                            </p>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                                <img
                                    src={IMG_REPAIR}
                                    alt="Weathered wood deck showing maintenance needs — gray boards, splits, and surface wear"
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {REPAIR_SERVICES.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#F6F5F2] border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm transition-all p-6" data-testid={`deck-repair-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <div className="w-12 h-12 bg-[#111111] text-[#FFD400] border border-[#111111] flex items-center justify-center mb-5">
                                    <I className="w-5 h-5" strokeWidth={2.25} />
                                </div>
                                <div className="font-display text-lg font-extrabold text-[#111812]">{t}</div>
                                <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>

                    {/* Warning signs */}
                    <div className="mt-16 grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-4">
                            <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Time to call us</div>
                            <h3 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-3xl sm:text-4xl">
                                Warning signs your deck needs help.
                            </h3>
                            <p className="mt-4 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed">
                                Catch any of these early and we can almost always repair instead of rebuild.
                            </p>
                        </div>
                        <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                            {WARNING_SIGNS.map((s) => (
                                <li key={s} className="bg-[#F6F5F2] border border-[#E2DFD8] px-4 py-3 flex items-start gap-2 text-sm font-semibold text-[#111812]">
                                    <Wrench className="w-4 h-4 text-[#E11D2A] shrink-0 mt-0.5" strokeWidth={2.5} />
                                    <span>{s}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Materials */}
            <section className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="aspect-[5/4] overflow-hidden border-2 border-[#111111] shadow-block bg-[#111111] max-w-md lg:max-w-none mx-auto">
                            <img
                                src={IMG_FINISHED}
                                alt="Finished deck with wood railings and stair detail — quality craftsmanship"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Materials</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111812] text-4xl sm:text-5xl">
                            Wood, composite, or hardwood — we&apos;ll match the right material to your budget.
                        </h2>
                        <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                            Every material has trade-offs. Here&apos;s the honest breakdown.
                        </p>
                        <div className="mt-8 overflow-x-auto border border-[#E2DFD8]">
                            <table className="w-full text-left text-sm" data-testid="decks-materials-table">
                                <thead className="bg-[#111111] text-[#F6F5F2]">
                                    <tr>
                                        <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Material</th>
                                        <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Lifespan</th>
                                        <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Look</th>
                                        <th className="font-['Manrope'] uppercase tracking-[0.18em] text-xs px-4 py-3 font-black">Price</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {MATERIALS.map((m, i) => (
                                        <tr key={m.name} className={i % 2 ? "bg-[#F6F5F2]" : "bg-white"}>
                                            <td className="px-4 py-3 font-display text-base font-extrabold text-[#111812]">{m.name}</td>
                                            <td className="px-4 py-3 font-['Manrope'] text-[#111812]">{m.life}</td>
                                            <td className="px-4 py-3 font-['Manrope'] text-[#5F5F5F]">{m.look}</td>
                                            <td className="px-4 py-3 font-display text-[#E11D2A] font-extrabold">{m.price}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust pillars */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-20">
                    <div className="text-center mb-12">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#FFD400]">Why pick us</div>
                        <h2 className="mt-3 font-['Anton'] uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl md:text-5xl">
                            Quality materials. Expert craftsmanship.
                            <br />
                            <span className="text-[#FFD400]">Guaranteed satisfaction.</span>
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F6F5F2]/15 border border-[#F6F5F2]/15">
                        {TRUST.map(({ i: I, t, d }) => (
                            <div key={t} className="bg-[#111111] p-7 text-center" data-testid={`decks-trust-${t.toLowerCase()}`}>
                                <div className="w-14 h-14 bg-[#E11D2A] border-2 border-[#F6F5F2] flex items-center justify-center mx-auto mb-5">
                                    <I className="w-6 h-6" strokeWidth={2.25} />
                                </div>
                                <div className="font-['Anton'] tracking-tight uppercase text-xl">{t}</div>
                                <div className="mt-2 font-['Manrope'] text-sm text-[#F6F5F2]/70 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-b-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Let&apos;s build your <span className="bg-[#E11D2A] text-[#F6F5F2] px-3 py-0.5">dream deck.</span>
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Free estimates. Honest pricing. Real Oregon contractor (CCB #232477) with 25 years on the tools.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="decks-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="decks-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                FREE ESTIMATE
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Tagline strip */}
            <section className="bg-[#111111] text-[#F6F5F2] border-b border-[#F6F5F2]/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-center">
                    <div className="font-['Manrope'] text-xs sm:text-sm font-black tracking-[0.22em] uppercase">
                        <HomeIcon className="inline w-4 h-4 mr-2 -mt-1 text-[#E11D2A]" strokeWidth={2.5} />
                        Small jobs. Big results. Done right the first time.
                    </div>
                </div>
            </section>

            {/* Image attribution */}
            <section className="bg-[#111111] text-[#F6F5F2]/55">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-5 text-[11px] font-['Manrope'] leading-relaxed">
                    Example deck photos via Wikimedia Commons (CC BY-SA) &amp; user-supplied / Pexels (free use). Used for illustration of build &amp; repair types; not all are photos of jobs we&apos;ve personally completed.
                </div>
            </section>

            <Footer />
        </div>
    );
}
